
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { LoginProvider, MembersRegistration } from '../../../utils/models';
import RadioGroup from '../../utils/RadioGroup.vue';
import RadioItem from '../../utils/RadioItem.vue';
import CheckBox from '../../utils/CheckBox.vue';
import Alert from '../../utils/Alert.vue';
import EventEmitter from 'events';

@Component({
  components: {
    RadioGroup,
    RadioItem,
    CheckBox,
    Alert,
  },
})
export default class MemberRegistrationForm extends Vue {
  @Prop({ default: [] })
  providers!: LoginProvider[];
  memberAuthType = '';
  memberCandidates = '';
  selectedProvider?: LoginProvider;
  createZawaroomAccount = false;
  placeholders: Record<string, string> = {
    default: '入力例) user1@sample.com, user2@sample.com',
    'create account': `入力例) user1@sample.com password
user2@sample.com password`,
  };
  errorMessages: string[] = [];
  alertEventEmitter = new EventEmitter();
  autoDeleteUsers = true;

  get placeholder() {
    return this.placeholders[this.isAccountCreatable ? 'create account' : 'default'];
  }

  get isAccountCreatable() {
    return !!(
      this.memberAuthType &&
      this.selectedProvider &&
      this.selectedProvider.isB2cTenant &&
      this.selectedProvider.allowAccountCreationInAdvance
    );
  }

  onAuthTypeChange(id: string) {
    this.memberAuthType = id;
    this.selectedProvider = this.providers.find((p) => p.id === id);
    if (!this.isAccountCreatable) {
      this.createZawaroomAccount = false;
    }
  }
  onCheckAccountCreation() {
    this.createZawaroomAccount = !this.createZawaroomAccount;
  }
  onCheckAutoDeleteUsers() {
    this.autoDeleteUsers = !this.autoDeleteUsers;
  }
  get canRegisterMember() {
    return this.memberAuthType !== '' && this.memberCandidates !== '';
  }

  addMember() {
    this.errorMessages = [];
    const emailsDelimiter = /[,\s\t][\s]*/; //区切り文字の後にスペースがあってもOK
    const accountDelimiter = /[\s\t]+/;

    if (this.isAccountCreatable) {
      const { isValid, invalidAccountRegistrationFormat } = this.validateAccountRegistrationFormat(
        this.memberCandidates,
        accountDelimiter,
      );
      if (!isValid) {
        invalidAccountRegistrationFormat.forEach((invalidFormat) => {
          if (typeof invalidFormat.email === 'string') {
            this.errorMessages.push(
              `${invalidFormat.index + 1}行目のメールアドレス${invalidFormat.email}が正しくありません。`,
            );
          } else if (typeof invalidFormat.password === 'string') {
            this.errorMessages.push(
              `${invalidFormat.index + 1}行目のパスワード${invalidFormat.password}が正しくありません。`,
            );
          }
        });
        this.$nextTick(() => {
          //更新内容がレンダリングされてからじゃないと更新できないためnextTickで遅らせる。
          this.alertEventEmitter.emit('updateHeight');
          this.alertEventEmitter.emit('shake');
        });
        return;
      }
    } else {
      const { isValid, invalidEmailFormat } = this.validateEmailsFormat(this.memberCandidates, emailsDelimiter);
      if (!isValid) {
        invalidEmailFormat.forEach((invalidFormat) => {
          this.errorMessages.push(
            `${invalidFormat.index + 1}個目のメールアドレス${invalidFormat.email}が正しくありません。`,
          );
        });
        return;
      }
    }
    if (!this.canRegisterMember) {
      return;
    }
    let members: MembersRegistration['members'];
    const autoDelete = this.$store.getters.isTrialSite && this.autoDeleteUsers;
    if (this.createZawaroomAccount) {
      members = this.memberCandidates
        .split('\n')
        .filter((line) => !!line)
        .map((line) => {
          const [email, password] = line.split(accountDelimiter);
          return { email, password, authType: this.memberAuthType, isSitesAdmin: false, autoDelete };
        });
    } else {
      members = this.memberCandidates
        .split(emailsDelimiter)
        .filter((v) => v)
        .map((e) => ({ email: e, authType: this.memberAuthType, isSitesAdmin: false, autoDelete }));
    }
    const membersCreation: MembersRegistration = {
      members,
      createZawaroomAccount: this.createZawaroomAccount,
      providerId: this.memberAuthType,
    };
    this.onRegister(membersCreation);
    this.memberCandidates = '';
  }

  private validateEmailsFormat(input: string, delimiter: RegExp) {
    const emailRegex = /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/;
    const emails = input.split(delimiter);
    type InvalidEmailFormat = { email: string; index: number };
    const invalidFormats: InvalidEmailFormat[] = [];
    emails.forEach((email, i) => {
      if (!emailRegex.test(email)) {
        invalidFormats.push({ index: i, email });
      }
    });
    return {
      isValid: invalidFormats.length === 0,
      invalidEmailFormat: invalidFormats,
    };
  }

  private validateAccountRegistrationFormat(input: string, delimiter: RegExp) {
    const emailRegex = /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/;
    const passwordRegex = /^[ -~]{8,64}$/; //半角英数記号が8~64文字あるかどうか

    const lines = input.split('\n');
    type InvalidAccountRegistrationFormat = {
      index: number;
      email?: string;
      password?: string;
    };
    const invalidFormats: InvalidAccountRegistrationFormat[] = [];
    lines.forEach((line, i) => {
      if (!line) return; //空行はスキップ
      const [email, password] = line.split(delimiter);
      if (!emailRegex.test(email)) {
        invalidFormats.push({ index: i, email });
        return;
      }
      if (!passwordRegex.test(password)) {
        invalidFormats.push({ index: i, password });
        return;
      }
      //大文字、小文字、数字、記号のうち3つを含めるという条件に合致するか調べる
      const passwordConditions = {
        upperCase: /[A-Z]/,
        lowerCase: /[a-z]/,
        numeric: /\d/,
        symbol: /[!-/:-@]/,
      };
      const passwordConditionsCount = Object.values(passwordConditions).reduce((count, condition) => {
        return count + (condition.test(password) ? 1 : 0);
      }, 0);
      //条件を満たさない場合はエラーとして追加
      if (passwordConditionsCount < 3) {
        invalidFormats.push({ index: i, password });
      }
    });
    return {
      isValid: invalidFormats.length === 0,
      invalidAccountRegistrationFormat: invalidFormats,
    };
  }

  onCandidatesTextareaKeyDown(event: KeyboardEvent) {
    //Tabを押すとフォーカスが移動するため、挙動を制御
    if (event.key === 'Tab') {
      const textarea = event.target as HTMLTextAreaElement | null;
      if (!textarea) return;
      textarea.setRangeText('\t', textarea.selectionStart, textarea.selectionEnd, 'end');
      this.memberCandidates = textarea.value; //memberCandidatesを更新しないと、insertしたTab込みの値をVueがトラッキングできない。
      event.preventDefault();
    }
  }

  @Emit('onRegister')
  onRegister(membersCreation: MembersRegistration) {}
}
